import type { ExerciseDef } from './types'

// ─── 7.4 Backprop for whole layers ────────────────────────────────────────────

export const linearBackward: ExerciseDef = {
  starter: `// A layer works on a whole batch at once:  Y = X·W + b
//   X: batch × inputs     W: inputs × outputs     b: 1 × outputs     Y: batch × outputs

function linearForward(X: number[][], W: number[][], b: number[]): number[][] {
  return mat.mul(X, W).map(row => row.map((v, j) => v + b[j]))
}

// linearBackward(X, W, dY): given dY = ∂L/∂Y (same shape as Y), return the three gradients.
//   dW = Xᵀ · dY            (inputs × outputs — the same shape as W)
//   dX = dY · Wᵀ            (batch × inputs   — the same shape as X)
//   db = the column sums of dY  (one per output — the same shape as b)
function linearBackward(X: number[][], W: number[][], dY: number[][]): { dW: number[][]; dX: number[][]; db: number[] } {
  return { dW: W, dX: X, db: [] }
}

const X = [[1, 2], [3, 4], [5, 6]]
const W = [[0.1, -0.2, 0.3], [0.4, 0.5, -0.6]]
const dY = [[1, 0, 0], [0, 1, 0], [0, 0, 1]]
console.log(linearBackward(X, W, dY))
`,
  solution: `function linearForward(X: number[][], W: number[][], b: number[]): number[][] {
  return mat.mul(X, W).map(row => row.map((v, j) => v + b[j]))
}

function linearBackward(X: number[][], W: number[][], dY: number[][]): { dW: number[][]; dX: number[][]; db: number[] } {
  const dW = mat.mul(mat.transpose(X), dY)
  const dX = mat.mul(dY, mat.transpose(W))
  const db = dY[0].map((_, j) => dY.reduce((s, row) => s + row[j], 0))
  return { dW, dX, db }
}

const X = [[1, 2], [3, 4], [5, 6]]
const W = [[0.1, -0.2, 0.3], [0.4, 0.5, -0.6]]
const dY = [[1, 0, 0], [0, 1, 0], [0, 0, 1]]
console.log(linearBackward(X, W, dY))
`,
  tests: `
// Check against a numerical gradient of L = Σ Y ⊙ G (so that ∂L/∂Y = G).
const __X = [[1, 2], [3, -1], [0.5, 4]]
const __W = [[0.3, -0.7, 0.2], [0.9, 0.1, -0.4]]
const __b = [0.1, 0.2, 0.3]
const __G = [[1, -2, 0.5], [0.3, 0.7, -1], [2, 0, 1]]
const __L = (X, W, b) => linearForward(X, W, b).reduce((s, row, i) => s + row.reduce((t, v, j) => t + v * __G[i][j], 0), 0)
const __num = (f, M, i, j) => { const h = 1e-6; const up = M.map(r => r.slice()); const dn = M.map(r => r.slice()); up[i][j] += h; dn[i][j] -= h; return (f(up) - f(dn)) / (2 * h) }
const __g = linearBackward(__X, __W, __G)
test("dW has the shape of W", () => expect([__g.dW.length, __g.dW[0].length], "shape of dW").toEqual([2, 3]))
test("dW matches the numerical gradient", () => expect(__g.dW[1][2], "dW[1][2]").toBeCloseTo(__num(W => __L(__X, W, __b), __W, 1, 2), 1e-5))
test("dX matches the numerical gradient", () => expect(__g.dX[2][0], "dX[2][0]").toBeCloseTo(__num(X => __L(X, __W, __b), __X, 2, 0), 1e-5))
test("db is the column sums of dY", () => expect(__g.db, "db").toBeCloseTo([3.3, -1.3, 0.5], 1e-9))
`,
}

export const reluBackward: ExerciseDef = {
  starter: `// ReLU works entry by entry: max(0, z).
function reluForward(Z: number[][]): number[][] {
  return Z.map(row => row.map(z => Math.max(0, z)))
}

// reluBackward(Z, dA): the gradient flows through wherever z was positive, and is blocked (0) everywhere else.
function reluBackward(Z: number[][], dA: number[][]): number[][] {
  return dA
}

console.log(reluBackward([[1, -2], [0.5, 3]], [[10, 20], [30, 40]]))   // [[10, 0], [30, 40]]
`,
  solution: `function reluForward(Z: number[][]): number[][] {
  return Z.map(row => row.map(z => Math.max(0, z)))
}

function reluBackward(Z: number[][], dA: number[][]): number[][] {
  return dA.map((row, i) => row.map((g, j) => (Z[i][j] > 0 ? g : 0)))
}

console.log(reluBackward([[1, -2], [0.5, 3]], [[10, 20], [30, 40]]))
`,
  tests: `
test("blocks the gradient where z ≤ 0", () => expect(reluBackward([[1, -2], [0.5, 3]], [[10, 20], [30, 40]]), "reluBackward").toEqual([[10, 0], [30, 40]]))
test("z exactly 0 counts as blocked", () => expect(reluBackward([[0]], [[5]]), "reluBackward([[0]], [[5]])").toEqual([[0]]))
`,
}

export const xorMatrix: ExerciseDef = {
  starter: `// Train a 2 → 8 → 1 network on XOR using ONLY matrix operations — no autograd.
const X = [[0, 0], [0, 1], [1, 0], [1, 1]]
const Y = [[0], [1], [1], [0]]

random.seed(4)
let W1 = mat.random(2, 8, 1), b1 = new Array(8).fill(0)
let W2 = mat.random(8, 1, 0.5), b2 = [0]
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))
const colSums = (M: number[][]) => M[0].map((_, j) => M.reduce((s, r) => s + r[j], 0))
const lr = 0.5

for (let step = 0; step < 2000; step++) {
  // forward
  const Z1 = mat.mul(X, W1).map(r => r.map((v, j) => v + b1[j]))
  const A1 = Z1.map(r => r.map(z => Math.max(0, z)))
  const Z2 = mat.mul(A1, W2).map(r => r.map((v, j) => v + b2[j]))
  const P = Z2.map(r => r.map(sigmoid))

  // backward. With sigmoid + binary cross-entropy, ∂L/∂Z2 is simply (P − Y) / batch size:
  const dZ2 = P.map((r, i) => r.map((p, j) => (p - Y[i][j]) / X.length))
  // TODO 1: dW2 = A1ᵀ · dZ2,   db2 = column sums of dZ2
  // TODO 2: dA1 = dZ2 · W2ᵀ,   then dZ1 = dA1 where Z1 > 0, else 0
  // TODO 3: dW1 = Xᵀ · dZ1,    db1 = column sums of dZ1
  // TODO 4: step every parameter:  W ← W − lr · dW  (use mat.add and mat.scale), same for the biases
}

function predict(x: number[]): number {
  const a1 = mat.apply(mat.transpose(W1), x).map((v, j) => Math.max(0, v + b1[j]))
  return sigmoid(vec.dot(a1, W2.map(r => r[0])) + b2[0])
}
console.log(X.map(x => round(predict(x), 3)))
`,
  solution: `const X = [[0, 0], [0, 1], [1, 0], [1, 1]]
const Y = [[0], [1], [1], [0]]

random.seed(4)
let W1 = mat.random(2, 8, 1), b1 = new Array(8).fill(0)
let W2 = mat.random(8, 1, 0.5), b2 = [0]
const sigmoid = (z: number) => 1 / (1 + Math.exp(-z))
const colSums = (M: number[][]) => M[0].map((_, j) => M.reduce((s, r) => s + r[j], 0))
const lr = 0.5

for (let step = 0; step < 2000; step++) {
  const Z1 = mat.mul(X, W1).map(r => r.map((v, j) => v + b1[j]))
  const A1 = Z1.map(r => r.map(z => Math.max(0, z)))
  const Z2 = mat.mul(A1, W2).map(r => r.map((v, j) => v + b2[j]))
  const P = Z2.map(r => r.map(sigmoid))

  const dZ2 = P.map((r, i) => r.map((p, j) => (p - Y[i][j]) / X.length))
  const dW2 = mat.mul(mat.transpose(A1), dZ2)
  const db2 = colSums(dZ2)
  const dA1 = mat.mul(dZ2, mat.transpose(W2))
  const dZ1 = dA1.map((r, i) => r.map((g, j) => (Z1[i][j] > 0 ? g : 0)))
  const dW1 = mat.mul(mat.transpose(X), dZ1)
  const db1 = colSums(dZ1)

  W1 = mat.add(W1, mat.scale(dW1, -lr))
  b1 = b1.map((v, j) => v - lr * db1[j])
  W2 = mat.add(W2, mat.scale(dW2, -lr))
  b2 = b2.map((v, j) => v - lr * db2[j])
}

function predict(x: number[]): number {
  const a1 = mat.apply(mat.transpose(W1), x).map((v, j) => Math.max(0, v + b1[j]))
  return sigmoid(vec.dot(a1, W2.map(r => r[0])) + b2[0])
}
console.log(X.map(x => round(predict(x), 3)))
`,
  tests: `
test("XOR(0, 0) ≈ 0", () => expect(predict([0, 0]), "predict([0, 0])").toBeLessThan(0.1))
test("XOR(0, 1) ≈ 1", () => expect(predict([0, 1]), "predict([0, 1])").toBeGreaterThan(0.9))
test("XOR(1, 0) ≈ 1", () => expect(predict([1, 0]), "predict([1, 0])").toBeGreaterThan(0.9))
test("XOR(1, 1) ≈ 0", () => expect(predict([1, 1]), "predict([1, 1])").toBeLessThan(0.1))
`,
}

// ─── 7.7 Training deep networks ───────────────────────────────────────────────

export const tensorAutograd: ExerciseDef = {
  starter: `// Your Value engine tracks single numbers. A Tensor is the same idea for whole matrices:
// every operation remembers how to pass gradients back, and .backward() runs the chain rule.
const X = Tensor.from([[1, 2], [3, -1], [0.5, 4]])
const W = Tensor.from([[0.3, -0.7, 0.2], [0.9, 0.1, -0.4]])
const b = Tensor.from([[0.1, 0.2, 0.3]])
const G = Tensor.from([[1, -2, 0.5], [0.3, 0.7, -1], [2, 0, 1]])

const Y = X.matmul(W).add(b)       // the layer
const L = Y.mul(G).sum()           // a made-up loss with ∂L/∂Y = G
L.backward()

// The Tensor engine agrees with the formula you just derived:  dW = Xᵀ · G
console.log("W.grad:     ", W.toArray().map((_, i) => Array.from(W.grad.slice(i * 3, i * 3 + 3))))
console.log("Xᵀ · G:     ", mat.mul(mat.transpose(X.toArray()), G.toArray()))
console.log("b.grad:     ", Array.from(b.grad), "(column sums of G)")

// Linear bundles W and b; Adam is a smarter optimiser (more in the optimizers lesson).
const layer = new Linear(196, 10)
console.log("a Linear(196, 10) has", layer.parameters().map(p => p.shape.join("×")).join(" and "), "parameters")
`,
}

const DIGITS_SMALL = `random.seed(42)
const D = data.digits(800)
const trainX = D.X.slice(0, 600), trainY = D.y.slice(0, 600)
const valX = D.X.slice(600), valY = D.y.slice(600)

function accuracy(forward: (x: Tensor) => Tensor, X: number[][], Y: number[]): number {
  const out = forward(Tensor.from(X))
  return Y.filter((y, i) => vec.argmax(out.row(i)) === y).length / Y.length
}

// Trains any network for some epochs with mini-batches of 32 (plain SGD, or Adam). Returns the last batch's loss.
function train(net: { forward: (x: Tensor) => Tensor; params: Tensor[] }, epochs: number, lr: number, adam = false): number {
  const opt = adam ? new Adam(net.params, { lr }) : new SGD(net.params, { lr })
  let last = 0
  for (let e = 0; e < epochs; e++) {
    const order = random.shuffle(range(trainX.length))
    for (let b = 0; b < order.length; b += 32) {
      const idx = order.slice(b, b + 32)
      const loss = net.forward(Tensor.from(idx.map(i => trainX[i]))).crossEntropy(idx.map(i => trainY[i]))
      opt.zeroGrad()
      loss.backward()
      opt.step()
      last = loss.item()
    }
  }
  return last
}`

export const initExperiment: ExerciseDef = {
  starter: `${DIGITS_SMALL}

// deepNet(std): 196 → 64 → 64 → 64 → 64 → 64 → 64 → 10, tanh after every hidden layer,
// with every weight matrix drawn from Tensor.randn(rows, cols, std).
function deepNet(std: number): { forward: (x: Tensor) => Tensor; params: Tensor[] } {
  random.seed(3)
  const sizes = [196, 64, 64, 64, 64, 64, 64, 10]
  const layers: Linear[] = []
  for (let i = 0; i < sizes.length - 1; i++) {
    const layer = new Linear(sizes[i], sizes[i + 1])
    // TODO: replace layer.W with a fresh Tensor.randn of the same shape, using this std
    layers.push(layer)
  }
  const forward = (x: Tensor) => {
    let h = x
    // TODO: apply each layer; use .tanh() after every layer except the last
    return h
  }
  return { forward, params: layers.flatMap(l => l.parameters()) }
}

const results: Record<string, number> = {}
for (const [name, std] of [["tiny (0.01)", 0.01], ["Xavier (1/√64)", 1 / 8], ["huge (1)", 1]] as const) {
  const net = deepNet(std)
  train(net, 3, 0.1)
  results[name] = accuracy(net.forward, valX, valY)
}
console.table(results)
`,
  solution: `${DIGITS_SMALL}

function deepNet(std: number): { forward: (x: Tensor) => Tensor; params: Tensor[] } {
  random.seed(3)
  const sizes = [196, 64, 64, 64, 64, 64, 64, 10]
  const layers: Linear[] = []
  for (let i = 0; i < sizes.length - 1; i++) {
    const layer = new Linear(sizes[i], sizes[i + 1])
    layer.W = Tensor.randn(sizes[i], sizes[i + 1], std)
    layers.push(layer)
  }
  const forward = (x: Tensor) => {
    let h = x
    layers.forEach((l, i) => {
      h = l.forward(h)
      if (i < layers.length - 1) h = h.tanh()
    })
    return h
  }
  return { forward, params: layers.flatMap(l => l.parameters()) }
}

const results: Record<string, number> = {}
for (const [name, std] of [["tiny (0.01)", 0.01], ["Xavier (1/√64)", 1 / 8], ["huge (1)", 1]] as const) {
  const net = deepNet(std)
  train(net, 3, 0.1)
  results[name] = accuracy(net.forward, valX, valY)
}
console.table(results)
`,
  tests: `
test("tiny weights: the signal dies — no better than guessing", () => expect(results["tiny (0.01)"], "accuracy").toBeLessThan(0.3))
test("huge weights: tanh saturates — barely learns", () => expect(results["huge (1)"], "accuracy").toBeLessThan(0.4))
test("well-scaled weights learn", () => expect(results["Xavier (1/√64)"], "accuracy").toBeGreaterThan(0.6))
`,
}

export const residualNorm: ExerciseDef = {
  starter: `${DIGITS_SMALL}

// A 20-layer network, two ways.
function makeNet(residual: boolean): { forward: (x: Tensor) => Tensor; params: Tensor[] } {
  random.seed(3)
  const width = 48
  const inp = new Linear(196, width)
  const blocks = range(20).map(() => new Linear(width, width))
  const gammas = blocks.map(() => Tensor.ones(1, width))
  const betas = blocks.map(() => Tensor.zeros(1, width))
  const finalG = Tensor.ones(1, width), finalB = Tensor.zeros(1, width)
  const out = new Linear(width, 10)
  const forward = (x: Tensor) => {
    let h = inp.forward(x).tanh()
    blocks.forEach((block, i) => {
      if (!residual) h = block.forward(h).tanh()
      // TODO: turn this into a residual block with layer norm:  h ← h + tanh(block(layerNorm(h)))
      //       (h.layerNorm(gammas[i], betas[i]) normalises each row)
      else h = block.forward(h).tanh()
    })
    // the residual stream keeps growing as blocks add to it, so normalise it once more at the end
    return out.forward(residual ? h.layerNorm(finalG, finalB) : h)
  }
  const params = [inp, ...blocks, out].flatMap(l => l.parameters()).concat(residual ? [...gammas, ...betas, finalG, finalB] : [])
  return { forward, params }
}

const plain = makeNet(false)
random.seed(1)
train(plain, 3, 2e-3, true)
const residual = makeNet(true)
random.seed(1)
train(residual, 3, 2e-3, true)
const plainAcc = accuracy(plain.forward, valX, valY)
const residualAcc = accuracy(residual.forward, valX, valY)
console.log("plain 20 layers:", plainAcc, "  residual + norm:", residualAcc)
`,
  solution: `${DIGITS_SMALL}

function makeNet(residual: boolean): { forward: (x: Tensor) => Tensor; params: Tensor[] } {
  random.seed(3)
  const width = 48
  const inp = new Linear(196, width)
  const blocks = range(20).map(() => new Linear(width, width))
  const gammas = blocks.map(() => Tensor.ones(1, width))
  const betas = blocks.map(() => Tensor.zeros(1, width))
  const finalG = Tensor.ones(1, width), finalB = Tensor.zeros(1, width)
  const out = new Linear(width, 10)
  const forward = (x: Tensor) => {
    let h = inp.forward(x).tanh()
    blocks.forEach((block, i) => {
      if (!residual) h = block.forward(h).tanh()
      else h = h.add(block.forward(h.layerNorm(gammas[i], betas[i])).tanh())
    })
    return out.forward(residual ? h.layerNorm(finalG, finalB) : h)
  }
  const params = [inp, ...blocks, out].flatMap(l => l.parameters()).concat(residual ? [...gammas, ...betas, finalG, finalB] : [])
  return { forward, params }
}

const plain = makeNet(false)
random.seed(1)
train(plain, 3, 2e-3, true)
const residual = makeNet(true)
random.seed(1)
train(residual, 3, 2e-3, true)
const plainAcc = accuracy(plain.forward, valX, valY)
const residualAcc = accuracy(residual.forward, valX, valY)
console.log("plain 20 layers:", plainAcc, "  residual + norm:", residualAcc)
`,
  tests: `
test("the residual network learns much faster", () => expect(residualAcc, "residual accuracy").toBeGreaterThan(plainAcc + 0.25))
`,
}

// ─── 7.8 Regularization ───────────────────────────────────────────────────────

const RIDGE_SETUP = `random.seed(8)
const f = (x: number) => Math.sin(1.3 * x) + 0.25 * x
const trainX = range(12).map(() => random.uniform(-3, 3)), trainY = trainX.map(x => f(x) + random.normal(0, 0.3))
const valX = range(60).map(() => random.uniform(-3, 3)), valY = valX.map(x => f(x) + random.normal(0, 0.3))

const DEGREE = 9
const feats = (x: number) => range(DEGREE + 1).map(k => (x / 3) ** k)

// Solves the square system M·w = rhs (M has the right-hand side glued on as its last column).
function solve(M: number[][]): number[] {
  const d = M.length
  for (let c = 0; c < d; c++) {
    let p = c
    for (let r = c + 1; r < d; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r
    ;[M[c], M[p]] = [M[p], M[c]]
    for (let r = 0; r < d; r++) {
      if (r === c) continue
      const q = M[r][c] / M[c][c]
      for (let k = c; k <= d; k++) M[r][k] -= q * M[c][k]
    }
  }
  return M.map((row, i) => row[d] / row[i])
}

const mse = (g: (x: number) => number, X: number[], Y: number[]) => vec.mean(X.map((x, i) => (g(x) - Y[i]) ** 2))`

export const ridge: ExerciseDef = {
  starter: `${RIDGE_SETUP}

// fitRidge(lambda): least squares with an L2 penalty  λ·Σ wₖ²  on the weights.
// The best weights solve (AᵀA + λI)·w = Aᵀy — the normal equations with λ added to the diagonal.
// (Don't penalise the constant term w₀: add λ only for k ≥ 1.)
function fitRidge(lambda: number): (x: number) => number {
  const A = trainX.map(feats)
  const d = DEGREE + 1
  const M = range(d).map(i => [
    ...range(d).map(j => A.reduce((s, row) => s + row[i] * row[j], 0)),   // ← TODO: + λ on the diagonal (i === j, i > 0)
    A.reduce((s, row, n) => s + row[i] * trainY[n], 0),
  ])
  const w = solve(M)
  return x => vec.dot(feats(x), w)
}

const lambdas = [0, 1e-4, 1e-3, 1e-2, 0.1, 1, 10]
for (const l of lambdas) {
  const g = fitRidge(l)
  console.log("λ =", String(l).padEnd(6), " train:", round(mse(g, trainX, trainY), 4), "  validation:", round(mse(g, valX, valY), 4))
}
plot.fn([f, fitRidge(0), fitRidge(0.01)], [-3, 3], { title: "truth, no penalty, λ = 0.01" })
`,
  solution: `${RIDGE_SETUP}

function fitRidge(lambda: number): (x: number) => number {
  const A = trainX.map(feats)
  const d = DEGREE + 1
  const M = range(d).map(i => [
    ...range(d).map(j => A.reduce((s, row) => s + row[i] * row[j], 0) + (i === j && i > 0 ? lambda : 0)),
    A.reduce((s, row, n) => s + row[i] * trainY[n], 0),
  ])
  const w = solve(M)
  return x => vec.dot(feats(x), w)
}

const lambdas = [0, 1e-4, 1e-3, 1e-2, 0.1, 1, 10]
for (const l of lambdas) {
  const g = fitRidge(l)
  console.log("λ =", String(l).padEnd(6), " train:", round(mse(g, trainX, trainY), 4), "  validation:", round(mse(g, valX, valY), 4))
}
plot.fn([f, fitRidge(0), fitRidge(0.01)], [-3, 3], { title: "truth, no penalty, λ = 0.01" })
`,
  tests: `
test("the penalty raises the training error a little", () => expect(mse(fitRidge(0.1), trainX, trainY), "train error with λ = 0.1").toBeGreaterThan(mse(fitRidge(0), trainX, trainY)))
test("…but slashes the validation error", () => expect(mse(fitRidge(0.01), valX, valY), "validation error with λ = 0.01").toBeLessThan(mse(fitRidge(0), valX, valY) / 10))
test("too much penalty underfits", () => expect(mse(fitRidge(10), valX, valY), "validation error with λ = 10").toBeGreaterThan(mse(fitRidge(0.01), valX, valY)))
`,
}

export const earlyStopping: ExerciseDef = {
  starter: `// 1. bestEpoch(valLosses): the index of the lowest validation loss.
function bestEpoch(valLosses: number[]): number {
  return valLosses.length - 1
}

// 2. shouldStop(valLosses, patience): true once the best loss is more than "patience" epochs in the past.
//    shouldStop([3, 2, 2.5, 2.6], 2) → true   (best was epoch 1; epochs 2 and 3 didn't beat it)
function shouldStop(valLosses: number[], patience: number): boolean {
  return false
}

// A real run: a small network, 120 digits with 30% WRONG labels — easy to overfit.
random.seed(42)
const D = data.digits(450)
const Xs = D.X.slice(0, 120), Ys = D.y.slice(0, 120).map(y => (random.random() < 0.3 ? random.int(0, 10) : y))
const Xv = Tensor.from(D.X.slice(250)), Yv = D.y.slice(250)
random.seed(5)
const l1 = new Linear(196, 64), l2 = new Linear(64, 10)
const forward = (x: Tensor) => l2.forward(l1.forward(x).relu())
const opt = new Adam([...l1.parameters(), ...l2.parameters()], { lr: 3e-3 })
const valLosses: number[] = []
const chart = plot.live("validation loss")
for (let epoch = 0; epoch < 60; epoch++) {
  const order = random.shuffle(range(Xs.length))
  for (let b = 0; b < order.length; b += 20) {
    const idx = order.slice(b, b + 20)
    const loss = forward(Tensor.from(idx.map(i => Xs[i]))).crossEntropy(idx.map(i => Ys[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
  valLosses.push(forward(Xv).crossEntropy(Yv).item())
  chart.push(valLosses[epoch])
  if (shouldStop(valLosses, 8)) break
}
console.log("stopped after", valLosses.length, "epochs; best was epoch", bestEpoch(valLosses))
`,
  solution: `function bestEpoch(valLosses: number[]): number {
  return vec.argmin(valLosses)
}

function shouldStop(valLosses: number[], patience: number): boolean {
  return valLosses.length - 1 - bestEpoch(valLosses) > patience
}

random.seed(42)
const D = data.digits(450)
const Xs = D.X.slice(0, 120), Ys = D.y.slice(0, 120).map(y => (random.random() < 0.3 ? random.int(0, 10) : y))
const Xv = Tensor.from(D.X.slice(250)), Yv = D.y.slice(250)
random.seed(5)
const l1 = new Linear(196, 64), l2 = new Linear(64, 10)
const forward = (x: Tensor) => l2.forward(l1.forward(x).relu())
const opt = new Adam([...l1.parameters(), ...l2.parameters()], { lr: 3e-3 })
const valLosses: number[] = []
const chart = plot.live("validation loss")
for (let epoch = 0; epoch < 60; epoch++) {
  const order = random.shuffle(range(Xs.length))
  for (let b = 0; b < order.length; b += 20) {
    const idx = order.slice(b, b + 20)
    const loss = forward(Tensor.from(idx.map(i => Xs[i]))).crossEntropy(idx.map(i => Ys[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
  valLosses.push(forward(Xv).crossEntropy(Yv).item())
  chart.push(valLosses[epoch])
  if (shouldStop(valLosses, 8)) break
}
console.log("stopped after", valLosses.length, "epochs; best was epoch", bestEpoch(valLosses))
`,
  tests: `
test("bestEpoch finds the lowest loss", () => expect(bestEpoch([3, 2, 2.5, 1.9, 2.2]), "bestEpoch").toBe(3))
test("not yet: the best is only 2 epochs ago", () => expect(shouldStop([3, 2, 2.5, 2.6], 2), "shouldStop(…, 2)").toBe(false))
test("stop: 3 epochs without beating the best", () => expect(shouldStop([3, 2, 2.5, 2.6, 2.7], 2), "shouldStop(…, 2)").toBe(true))
test("keep going while it's still improving", () => expect(shouldStop([5, 4, 3, 2], 1), "shouldStop").toBe(false))
test("the real run stopped early", () => expect(valLosses.length, "epochs run").toBeLessThan(60))
`,
}

// ─── 7.10 Convolutions ────────────────────────────────────────────────────────

export const conv2d: ExerciseDef = {
  starter: `// 1. conv2d(img, k): slide the kernel k (kh × kw) over the image (no padding, step 1).
//    out[i][j] = Σₐ Σ_b img[i + a][j + b] · k[a][b]       → (H − kh + 1) × (W − kw + 1)
function conv2d(img: number[][], k: number[][]): number[][] {
  return []
}

// 2. maxPool(img, size): split into size×size blocks and keep each block's largest value.
function maxPool(img: number[][], size: number): number[][] {
  return []
}

const image = data.image()
const vertical = [[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]]
const edges = conv2d(image, vertical)
plot.heatmap(image, { title: "input" })
plot.heatmap(edges, { title: "vertical edges" })
plot.heatmap(maxPool(edges.map(r => r.map(Math.abs)), 2), { title: "max-pooled edge strength (half the size)" })
`,
  solution: `function conv2d(img: number[][], k: number[][]): number[][] {
  const H = img.length - k.length + 1
  const W = img[0].length - k[0].length + 1
  const out: number[][] = []
  for (let i = 0; i < H; i++) {
    const row: number[] = []
    for (let j = 0; j < W; j++) {
      let s = 0
      for (let a = 0; a < k.length; a++) for (let b = 0; b < k[0].length; b++) s += img[i + a][j + b] * k[a][b]
      row.push(s)
    }
    out.push(row)
  }
  return out
}

function maxPool(img: number[][], size: number): number[][] {
  const out: number[][] = []
  for (let i = 0; i + size <= img.length; i += size) {
    const row: number[] = []
    for (let j = 0; j + size <= img[0].length; j += size) {
      let m = -Infinity
      for (let a = 0; a < size; a++) for (let b = 0; b < size; b++) m = Math.max(m, img[i + a][j + b])
      row.push(m)
    }
    out.push(row)
  }
  return out
}

const image = data.image()
const vertical = [[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]]
const edges = conv2d(image, vertical)
plot.heatmap(image, { title: "input" })
plot.heatmap(edges, { title: "vertical edges" })
plot.heatmap(maxPool(edges.map(r => r.map(Math.abs)), 2), { title: "max-pooled edge strength (half the size)" })
`,
  tests: `
test("output size is (H − 2) × (W − 2) for a 3×3 kernel", () => {
  const __o = conv2d(range(5).map(() => range(6)), [[1, 0, 0], [0, 0, 0], [0, 0, 0]])
  expect([__o.length, __o[0].length], "shape").toEqual([3, 4])
})
test("a kernel with a single 1 copies the image (shifted)", () => expect(conv2d([[1, 2, 3], [4, 5, 6], [7, 8, 9]], [[0, 0], [0, 1]]), "conv2d").toEqual([[5, 6], [8, 9]]))
test("a vertical-edge kernel fires on a vertical edge", () => {
  const __img = [[0, 0, 1, 1], [0, 0, 1, 1], [0, 0, 1, 1]]
  expect(conv2d(__img, [[-1, 0, 1], [-1, 0, 1], [-1, 0, 1]]), "conv2d").toEqual([[3, 3]])
})
test("maxPool keeps each block's maximum", () => expect(maxPool([[1, 5, 2, 0], [3, 4, 8, 1], [0, 0, 1, 1], [9, 0, 1, 2]], 2), "maxPool").toEqual([[5, 8], [9, 2]]))
`,
}

export const im2col: ExerciseDef = {
  starter: `// The trick every deep-learning library uses: turn convolution into ONE matrix multiply.
// patches(img, k): every k×k patch of the image, flattened into a row (row by row, left to right).
//   For a 14×14 image and k = 3 that's 12 × 12 = 144 rows of 9 numbers.
function patches(img: number[][], k: number): number[][] {
  return []
}

function conv2d(img: number[][], k: number[][]): number[][] {
  const H = img.length - k.length + 1, W = img[0].length - k[0].length + 1
  return range(H).map(i => range(W).map(j => {
    let s = 0
    for (let a = 0; a < k.length; a++) for (let b = 0; b < k.length; b++) s += img[i + a][j + b] * k[a][b]
    return s
  }))
}

// Several kernels at once: stack them as the COLUMNS of a 9 × C matrix.
const kernels = [[[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]], [[-1, -2, -1], [0, 0, 0], [1, 2, 1]]]
const K = range(9).map(p => kernels.map(k => k[Math.floor(p / 3)][p % 3]))
const img = range(14).map(i => data.digits(1).X[0].slice(i * 14, i * 14 + 14))
const viaMatmul = mat.mul(patches(img, 3), K)   // 144 × 2: one column per kernel
console.log("matches conv2d:", Math.abs(viaMatmul[13][0] - conv2d(img, kernels[0])[1][1]) < 1e-12)
`,
  solution: `function patches(img: number[][], k: number): number[][] {
  const out: number[][] = []
  for (let i = 0; i + k <= img.length; i++) {
    for (let j = 0; j + k <= img[0].length; j++) {
      const p: number[] = []
      for (let a = 0; a < k; a++) for (let b = 0; b < k; b++) p.push(img[i + a][j + b])
      out.push(p)
    }
  }
  return out
}

function conv2d(img: number[][], k: number[][]): number[][] {
  const H = img.length - k.length + 1, W = img[0].length - k[0].length + 1
  return range(H).map(i => range(W).map(j => {
    let s = 0
    for (let a = 0; a < k.length; a++) for (let b = 0; b < k.length; b++) s += img[i + a][j + b] * k[a][b]
    return s
  }))
}

const kernels = [[[-1, 0, 1], [-2, 0, 2], [-1, 0, 1]], [[-1, -2, -1], [0, 0, 0], [1, 2, 1]]]
const K = range(9).map(p => kernels.map(k => k[Math.floor(p / 3)][p % 3]))
const img = range(14).map(i => data.digits(1).X[0].slice(i * 14, i * 14 + 14))
const viaMatmul = mat.mul(patches(img, 3), K)
console.log("matches conv2d:", Math.abs(viaMatmul[13][0] - conv2d(img, kernels[0])[1][1]) < 1e-12)
`,
  tests: `
test("a 4×4 image has 4 patches of size 3", () => expect(patches(range(4).map(i => range(4).map(j => i * 4 + j)), 3).length, "number of patches").toBe(4))
test("the first patch reads row by row", () => expect(patches(range(4).map(i => range(4).map(j => i * 4 + j)), 3)[0], "first patch").toEqual([0, 1, 2, 4, 5, 6, 8, 9, 10]))
test("patches × kernels equals convolution, everywhere", () => {
  const __img = range(6).map(i => range(5).map(j => Math.sin(i * 3 + j)))
  const __k = [[1, -2, 0.5], [0, 3, 1], [-1, 0, 2]]
  const __mm = mat.mul(patches(__img, 3), range(9).map(p => [__k[Math.floor(p / 3)][p % 3]])).map(r => r[0])
  expect(__mm, "patches · kernel").toBeCloseTo(conv2d(__img, __k).flat(), 1e-12)
})
`,
}

export const cnnDigits: ExerciseDef = {
  starter: `function patches(img: number[], size: number, k: number): number[][] {
  const out: number[][] = []
  for (let i = 0; i + k <= size; i++)
    for (let j = 0; j + k <= size; j++) {
      const p: number[] = []
      for (let a = 0; a < k; a++) for (let b = 0; b < k; b++) p.push(img[(i + a) * size + j + b])
      out.push(p)
    }
  return out
}

random.seed(42)
const D = data.digits(1800)
const P = D.X.map(x => patches(x, 14, 3))          // each digit → 144 patches × 9 pixels
const trainIdx = range(1500), testIdx = range(1500, 1800)

// A tiny CNN: 8 learned 3×3 kernels → ReLU → one Linear layer to 10 scores.
const C = 8, POS = 144
random.seed(1)
const K = Tensor.randn(9, C, 1 / 3)     // 8 kernels, one per column
const kb = Tensor.zeros(1, C)           // one bias per kernel
const head = new Linear(POS * C, 10)
const opt = new Adam([K, kb, ...head.parameters()], { lr: 3e-3 })

// forward(idx): a batch of digits → 10 scores each.
//  1. stack the patches of every digit in the batch:   Tensor.from(idx.flatMap(i => P[i]))   (B·144 × 9)
//  2. convolve all of them at once:   .matmul(K).add(kb).relu()                               (B·144 × 8)
//  3. one row per digit:              .reshape(idx.length, POS * C)                          (B × 1152)
//  4. scores:                         head.forward(…)                                        (B × 10)
function forward(idx: number[]): Tensor {
  return head.forward(Tensor.zeros(idx.length, POS * C))
}

for (let epoch = 0; epoch < 4; epoch++) {
  const order = random.shuffle(trainIdx)
  for (let b = 0; b < order.length; b += 32) {
    const idx = order.slice(b, b + 32)
    const loss = forward(idx).crossEntropy(idx.map(i => D.y[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
  console.log("epoch", epoch + 1, "done")
}
const scores = forward(testIdx)
const testAccuracy = testIdx.filter((i, r) => vec.argmax(scores.row(r)) === D.y[i]).length / testIdx.length
console.log("CNN test accuracy:", testAccuracy)
plot.heatmap(range(3).map(r => range(3).map(c => K.get(r * 3 + c, 0))), { title: "kernel 1, learned" })
`,
  solution: `function patches(img: number[], size: number, k: number): number[][] {
  const out: number[][] = []
  for (let i = 0; i + k <= size; i++)
    for (let j = 0; j + k <= size; j++) {
      const p: number[] = []
      for (let a = 0; a < k; a++) for (let b = 0; b < k; b++) p.push(img[(i + a) * size + j + b])
      out.push(p)
    }
  return out
}

random.seed(42)
const D = data.digits(1800)
const P = D.X.map(x => patches(x, 14, 3))
const trainIdx = range(1500), testIdx = range(1500, 1800)

const C = 8, POS = 144
random.seed(1)
const K = Tensor.randn(9, C, 1 / 3)
const kb = Tensor.zeros(1, C)
const head = new Linear(POS * C, 10)
const opt = new Adam([K, kb, ...head.parameters()], { lr: 3e-3 })

function forward(idx: number[]): Tensor {
  const feats = Tensor.from(idx.flatMap(i => P[i])).matmul(K).add(kb).relu()
  return head.forward(feats.reshape(idx.length, POS * C))
}

for (let epoch = 0; epoch < 4; epoch++) {
  const order = random.shuffle(trainIdx)
  for (let b = 0; b < order.length; b += 32) {
    const idx = order.slice(b, b + 32)
    const loss = forward(idx).crossEntropy(idx.map(i => D.y[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
  console.log("epoch", epoch + 1, "done")
}
const scores = forward(testIdx)
const testAccuracy = testIdx.filter((i, r) => vec.argmax(scores.row(r)) === D.y[i]).length / testIdx.length
console.log("CNN test accuracy:", testAccuracy)
plot.heatmap(range(3).map(r => range(3).map(c => K.get(r * 3 + c, 0))), { title: "kernel 1, learned" })
`,
  tests: `
test("the CNN reads over 94% of unseen digits", () => expect(testAccuracy, "testAccuracy").toBeGreaterThan(0.94))
`,
}

// ─── 7.11 Project: read handwriting ───────────────────────────────────────────

const DIGIT_PROJECT_SETUP = `random.seed(42)
const D = data.digits(2600)
const train = { X: D.X.slice(0, 2000), y: D.y.slice(0, 2000) }
const valid = { X: D.X.slice(2000, 2300), y: D.y.slice(2000, 2300) }
const holdout = { X: D.X.slice(2300), y: D.y.slice(2300) }`

const DIGIT_MODEL = `random.seed(7)
const l1 = new Linear(196, 128)
const l2 = new Linear(128, 10)
const params = [...l1.parameters(), ...l2.parameters()]
const forward = (x: Tensor, training = false) => l2.forward(l1.forward(x).relu().dropout(training ? 0.2 : 0))
const accuracy = (set: { X: number[][]; y: number[] }) => {
  const out = forward(Tensor.from(set.X))
  return set.y.filter((y, i) => vec.argmax(out.row(i)) === y).length / set.y.length
}`

export const digitsTrain: ExerciseDef = {
  starter: `${DIGIT_PROJECT_SETUP}

${DIGIT_MODEL}

// 1. snapshot(): a copy of every parameter's numbers (so we can go back to the best epoch).
function snapshot(): Float64Array[] {
  return []
}
// 2. restore(s): copy the saved numbers back into the parameters.
function restore(s: Float64Array[]): void {}

// 3. The training loop: Adam, mini-batches of 64, 12 epochs. After every epoch, measure validation
//    accuracy; whenever it's the best so far, take a snapshot. At the end, restore the best one.
const opt = new Adam(params, { lr: 2e-3 })
const chart = plot.live("validation accuracy")
let best = { acc: 0, epoch: -1, weights: snapshot() }
for (let epoch = 0; epoch < 12; epoch++) {
  const order = random.shuffle(range(train.X.length))
  for (let b = 0; b < order.length; b += 64) {
    const idx = order.slice(b, b + 64)
    const loss = forward(Tensor.from(idx.map(i => train.X[i])), true).crossEntropy(idx.map(i => train.y[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
  const acc = accuracy(valid)
  chart.push(acc)
  // TODO: keep the best snapshot
}
// TODO: restore the best weights

const holdoutAccuracy = accuracy(holdout)
console.log("best epoch:", best.epoch, " validation:", best.acc, " held-out test:", holdoutAccuracy)
`,
  solution: `${DIGIT_PROJECT_SETUP}

${DIGIT_MODEL}

function snapshot(): Float64Array[] {
  return params.map(p => p.data.slice())
}
function restore(s: Float64Array[]): void {
  params.forEach((p, i) => p.data.set(s[i]))
}

const opt = new Adam(params, { lr: 2e-3 })
const chart = plot.live("validation accuracy")
let best = { acc: 0, epoch: -1, weights: snapshot() }
for (let epoch = 0; epoch < 12; epoch++) {
  const order = random.shuffle(range(train.X.length))
  for (let b = 0; b < order.length; b += 64) {
    const idx = order.slice(b, b + 64)
    const loss = forward(Tensor.from(idx.map(i => train.X[i])), true).crossEntropy(idx.map(i => train.y[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
  const acc = accuracy(valid)
  chart.push(acc)
  if (acc > best.acc) best = { acc, epoch, weights: snapshot() }
}
restore(best.weights)

const holdoutAccuracy = accuracy(holdout)
console.log("best epoch:", best.epoch, " validation:", best.acc, " held-out test:", holdoutAccuracy)
`,
  tests: `
test("snapshot copies (doesn't share) the numbers", () => {
  const __s = snapshot()
  const __before = __s[0][0]
  params[0].data[0] += 1
  const __changed = __s[0][0] === __before
  params[0].data[0] -= 1
  expect(__changed, "snapshot unaffected by later changes").toBe(true)
})
test("restore puts the numbers back", () => {
  const __s = snapshot()
  params[1].data[0] += 5
  restore(__s)
  expect(params[1].data[0], "restored value").toBeCloseTo(__s[1][0])
})
test("the restored model scores what the best epoch scored", () => expect(accuracy(valid), "validation accuracy now").toBeCloseTo(best.acc))
test("about 95% on digits it has never seen", () => expect(holdoutAccuracy, "holdoutAccuracy").toBeGreaterThan(0.93))
`,
}

export const digitsErrors: ExerciseDef = {
  starter: `${DIGIT_PROJECT_SETUP}

${DIGIT_MODEL}

// (a quick version of Part 1's training)
const opt = new Adam(params, { lr: 2e-3 })
for (let epoch = 0; epoch < 8; epoch++) {
  const order = random.shuffle(range(train.X.length))
  for (let b = 0; b < order.length; b += 64) {
    const idx = order.slice(b, b + 64)
    const loss = forward(Tensor.from(idx.map(i => train.X[i])), true).crossEntropy(idx.map(i => train.y[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
}
const scores = forward(Tensor.from(holdout.X))
const predicted = holdout.y.map((_, i) => vec.argmax(scores.row(i)))

// 1. confusion(truth, pred): a 10×10 table — C[t][p] counts digits that were really t but predicted p.
function confusion(truth: number[], pred: number[]): number[][] {
  return range(10).map(() => new Array(10).fill(0))
}

// 2. worstPair(C): the most common mistake, as [true digit, predicted digit] (ignore the diagonal).
function worstPair(C: number[][]): [number, number] {
  return [0, 0]
}

const C = confusion(holdout.y, predicted)
plot.heatmap(C, { title: "confusion matrix (rows: true digit, columns: predicted)", rowLabels: range(10).map(String), colLabels: range(10).map(String) })
const [t, p] = worstPair(C)
console.log(\`most common mistake: a \${t} read as a \${p} (\${C[t][p]} times)\`)
const example = holdout.y.findIndex((y, i) => y === t && predicted[i] === p)
if (example >= 0) plot.heatmap(range(14).map(r => holdout.X[example].slice(r * 14, r * 14 + 14)), { title: \`a \${t} the network called a \${p}\` })
`,
  solution: `${DIGIT_PROJECT_SETUP}

${DIGIT_MODEL}

const opt = new Adam(params, { lr: 2e-3 })
for (let epoch = 0; epoch < 8; epoch++) {
  const order = random.shuffle(range(train.X.length))
  for (let b = 0; b < order.length; b += 64) {
    const idx = order.slice(b, b + 64)
    const loss = forward(Tensor.from(idx.map(i => train.X[i])), true).crossEntropy(idx.map(i => train.y[i]))
    opt.zeroGrad()
    loss.backward()
    opt.step()
  }
}
const scores = forward(Tensor.from(holdout.X))
const predicted = holdout.y.map((_, i) => vec.argmax(scores.row(i)))

function confusion(truth: number[], pred: number[]): number[][] {
  const C = range(10).map(() => new Array(10).fill(0))
  truth.forEach((t, i) => C[t][pred[i]]++)
  return C
}

function worstPair(C: number[][]): [number, number] {
  let best: [number, number] = [0, 1]
  for (let t = 0; t < 10; t++) for (let p = 0; p < 10; p++) if (t !== p && C[t][p] > C[best[0]][best[1]]) best = [t, p]
  return best
}

const C = confusion(holdout.y, predicted)
plot.heatmap(C, { title: "confusion matrix (rows: true digit, columns: predicted)", rowLabels: range(10).map(String), colLabels: range(10).map(String) })
const [t, p] = worstPair(C)
console.log(\`most common mistake: a \${t} read as a \${p} (\${C[t][p]} times)\`)
const example = holdout.y.findIndex((y, i) => y === t && predicted[i] === p)
if (example >= 0) plot.heatmap(range(14).map(r => holdout.X[example].slice(r * 14, r * 14 + 14)), { title: \`a \${t} the network called a \${p}\` })
`,
  tests: `
test("confusion counts true × predicted", () => {
  const __C = confusion([1, 1, 2, 3], [1, 7, 2, 3])
  expect([__C[1][1], __C[1][7], __C[2][2], __C[0][0]], "cells").toEqual([1, 1, 1, 0])
})
test("every holdout digit is counted once", () => expect(confusion(holdout.y, predicted).flat().reduce((a, b) => a + b, 0), "total").toBe(holdout.y.length))
test("worstPair ignores the diagonal", () => {
  const __C = range(10).map(i => range(10).map(j => (i === j ? 50 : 0)))
  __C[4][9] = 3
  __C[7][1] = 2
  expect(worstPair(__C), "worstPair").toEqual([4, 9])
})
`,
}
